// @ts-nocheck

import url from 'url';
import { cx } from '@emotion/css';
import * as R from 'ramda';
import * as React from 'react';
import type { ReactNode } from 'react';
import { withRouter } from 'react-router-dom';
import LockedPackagingIcon from '../../../main/components/nav/LockedPackagingIcon/LockedPackagingIcon';
import type { OnProductClickInput } from '../../../main/screens/ScreenNewSummary/newSummaryTypes';
import type { HistoryType } from '../../router/withRouter';

export type Tab = {
  component: React.ReactElement<any> | React.JSX.Element;
  disabled?: boolean;
  hide?: boolean;
  icon?: string;
  key: string;
  title: string | ReactNode;
  disableForAgencyUser?: boolean;
  beta?: boolean;
  display?: React.ReactElement<any>;
};

function getActiveTabIndexFromUrl(tabs: Array<Tab>, defaultIndex: number | null | undefined): number | null | undefined {
  const urlObject = url.parse(window.location.href, true);
  const query = urlObject.query;

  if (query) {
    var queryIndex = R.findIndex(R.propEq('key', query.tab))(tabs);

    if (queryIndex === -1) {
      return;
    }

    if (!tabs[queryIndex].disabled) {
      return queryIndex;
    }
  }

  return defaultIndex;
}

function getActiveTabIndexFromRoute(
  tabs: Array<Tab>,
  defaultIndex: number | null | undefined,
  matcher: (tab: Tab) => boolean
): number | null | undefined {
  const activeTabIndex = tabs.map(matcher).indexOf(true);
  return activeTabIndex === -1 ? defaultIndex : activeTabIndex;
}

export const NO_RIGHT_BORDER = 'no-right-border';
type TabProps = {
  /**
   * Tabs config
   */
  data: Array<Tab>;

  /**
   * Enable query string to override active tabs (e.g. ?tab={key})
   */
  enableQueryStringMatching?: boolean;

  /**
   * Enable router path matching to override active tabs (e.g. /foo/bar/myRoute)
   */
  enableRouterMatching?: (tab: Tab) => boolean;

  /**
   * Init active index
   */
  initIndex?: number;

  /**
   * Handler to be invoked when tab is clicked
   */
  onClickHandler?: (arg0: OnProductClickInput) => void;

  /**
   * Class name for tab container
   */
  tabContainerClassName?: string;

  /**
   * Test id for automation
   */
  testid?: string;

  /**
   * Updates query string to show active tab (e.g. ?tab={key})
   */
  updateQueryStringOnTabChange?: boolean;
  writeAccess?: boolean;
  styleVariant?: string;
  tabHeaderContainerClassName?: string;
  isDarklyNavigationEnabled?: boolean;
};
type RouterProps = {
  history: HistoryType;
};
type Props = TabProps & RouterProps;
type State = {
  activeIndex: number | null | undefined;
};

/**
 * Generic Tabs Component
 */

class Tabs extends React.PureComponent<Props, State> {
  static defaultProps = {
    enableQueryStringMatching: false,
    updateQueryStringOnTabChange: false,
    initIndex: 0,
    onClickHandler: R.identity,
    testid: 'tabs',
  };

  constructor(props: Props) {
    super(props);
    const { data, enableQueryStringMatching, enableRouterMatching, initIndex } = props;
    let activeIndex = initIndex;

    if (enableQueryStringMatching) {
      activeIndex = getActiveTabIndexFromUrl(data, initIndex);
    }

    if (enableRouterMatching) {
      activeIndex = getActiveTabIndexFromRoute(data, initIndex, enableRouterMatching);
    }

    this.state = {
      activeIndex,
    };
  }

  componentDidMount() {
    const { data, enableQueryStringMatching, enableRouterMatching, history } = this.props;
    const { activeIndex } = this.state;

    if (enableQueryStringMatching || enableRouterMatching) {
      history.listen(() => {
        let newActiveTabIndex;

        if (enableQueryStringMatching) {
          newActiveTabIndex = getActiveTabIndexFromUrl(data, activeIndex);
        }

        if (enableRouterMatching) {
          newActiveTabIndex = getActiveTabIndexFromRoute(data, activeIndex, enableRouterMatching);
        }

        if (newActiveTabIndex && newActiveTabIndex !== activeIndex) {
          this.setState({
            activeIndex: newActiveTabIndex,
          });
        }
      });
    }
  }

  // TODO we're setting state from props, anti-pattern
  componentWillReceiveProps(nextProps: Props) {
    const { data, enableQueryStringMatching, initIndex } = nextProps;

    if (enableQueryStringMatching) {
      const activeIndex = getActiveTabIndexFromUrl(data);

      if (!R.isNil(activeIndex)) {
        this.setState({
          activeIndex,
        });
        return;
      }
    }

    if (this.props.initIndex !== initIndex) {
      this.setState({
        activeIndex: initIndex,
      });
    }
  }

  _onClick = (item: Tab, tabIndex: number) => {
    if (item.disabled) {
      return;
    }

    this.setState({
      activeIndex: tabIndex,
    });
    this.props.onClickHandler &&
      this.props.onClickHandler({
        active: tabIndex,
        item,
      });

    if (this.props.updateQueryStringOnTabChange) {
      this.props.history.push(`${window.location.pathname}?tab=${item.key}`);
    }
  };

  /**
   * Render tabs header
   * @param  {Array} data tabs configuration
   * @return {Object} React node string
   */
  renderHeader = (data: Array<Tab>, isDarklyNavigationEnabled: boolean): React.ReactElement<any> => {
    const { styleVariant, tabHeaderContainerClassName, testid, writeAccess } = this.props;
    const { activeIndex } = this.state;
    const noRightBorder = styleVariant === NO_RIGHT_BORDER;
    const nodes = data.map((item: Tab, tabIndex: number) => {
      if (item.hide || (item.require_write_access && !writeAccess)) {
        return null;
      }

      const isActive = activeIndex === tabIndex;
      const isLastTab = tabIndex === data.length - 1;
      const title = item.display ? item.display : item.title;

      return (
        <div
          onClick={
            !(item.packaging?.disabled && isDarklyNavigationEnabled) ? () => this._onClick(item, tabIndex) : undefined
          }
          bindClickEvent={true}
          className={cx('tabs__item', {
            'tabs__item-inactive': !isActive || (item.packaging?.disabled && isDarklyNavigationEnabled),
            'tabs__item-disabled': !!item.disabled || (item.packaging?.disabled && isDarklyNavigationEnabled),
          })}
          key={tabIndex}
          data-testid={`${testid}-title-${item.title.replace(/ /g, '_').toLowerCase()}`}
        >
          <div
            className={cx('tabs__tab-title', {
              'tabs__tab-title--active': isActive,
              'no-right-border': isLastTab && noRightBorder,
            })}
          >
            {item.icon}
            {title}
            {item.packaging?.showIcon && isDarklyNavigationEnabled && <LockedPackagingIcon route={title} />}
            {item.beta && <span className={'tabs-beta-button'}>BETA</span>}
          </div>
          <div
            className={cx('tabs__active-marker', {
              'tabs__show-active-marker': isActive,
            })}
          />
        </div>
      );
    });
    return (
      <div className={cx('tabs__header-container', tabHeaderContainerClassName)}>
        <div
          className={cx('tabs__tabs-header', {
            'no-underline': noRightBorder,
          })}
        >
          {nodes}
        </div>
      </div>
    );
  };

  renderTab = (data: Array<Tab>): React.ReactElement<any> => {
    const activeIndexComponent =
      this.state.activeIndex && data[this.state.activeIndex] && data[this.state.activeIndex].component;
    const TabComponent = activeIndexComponent || data[0].component;
    return (
      <div className={cx('tabs__tab-container', this.props.tabContainerClassName)}>
        {typeof TabComponent === 'function' ? <TabComponent /> : TabComponent}
      </div>
    );
  };

  render() {
    const { testid, data, isDarklyNavigationEnabled = false } = this.props;

    if (!data || R.isEmpty(data)) {
      return null;
    }

    return (
      <div className="tabs" data-testid={testid}>
        {this.renderHeader(data, isDarklyNavigationEnabled)}
        {this.renderTab(data)}
      </div>
    );
  }
}

const TabsWithRouter: React.ComponentType<TabProps> = withRouter(Tabs);
export default TabsWithRouter;
